<!-- ══════════════════ Scatter & Bubble builder ══════════════════
     Mirrors the Bar builder: a family card (variant picker), a Chart-Data
     accordion (X / Y / Size / Color / Series drop-zone pickers), then the
     config-driven Format accordions. Renders client-side via the pure
     scatter-bubble option builder. -->
<div class="wf-section wf-section-plain" *ngIf="active">
  <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

  <div class="bridge-chart-settings bar-family-card">
    <div class="bridge-settings-heading"><mat-icon>scatter_plot</mat-icon><span>{{ host.layoutSectionTitle('scatterBubble', 'Scatter & bubble') }}</span></div>
    <small class="control-hint" *ngIf="host.layoutSectionHint('scatterBubble')">{{ host.layoutSectionHint('scatterBubble') }}</small>

    <!-- Orientation segmented toggle — sides are FULLY config-driven
         (scatterBubbleModes: label + icon + value); the Variant dropdown
         below lists only the active side's base variants. -->
    <div class="control-group" *ngIf="host.fieldEnabled('scatterBubble', 'mode')">
      <label>{{ host.fieldLabel('scatterBubble', 'mode', 'Orientation') }}</label>
      <div class="wf-step-mode-toggle wf-sb-mode-toggle" role="group" [attr.aria-label]="host.fieldLabel('scatterBubble', 'mode', 'Orientation')">
        <button type="button" *ngFor="let m of sbModes" [class.is-active]="sbMode === m.value" (click)="setSbMode(m.value)">
          <mat-icon *ngIf="m.icon">{{ m.icon }}</mat-icon> {{ m.label }}
        </button>
      </div>
    </div>

    <!-- Variant — only the active toggle side's BASIC variants (Scatter /
         Multi Scatter, or Bubble / Bubble Matrix); advanced ones live in
         the accordion below. -->
    <div class="control-group" *ngIf="host.fieldEnabled('scatterBubble', 'variant')">
      <label>{{ host.fieldLabel('scatterBubble', 'variant', 'Variant') }}</label>
      <ng-select
        #ssSbVariant
        class="ss-select"
        [class.is-open]="!!ssSbVariant.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="sbVariantItems"
        bindLabel="label"
        bindValue="value"
        [clearable]="true"
        [ngModel]="sbVariant"
        (ngModelChange)="sbVariant = $event ?? ''; onScatterBubbleChange()"
      ></ng-select>
      <small class="control-hint wf-variant-hint" *ngIf="sbVariantHint">{{ sbVariantHint }}</small>
    </div>

    <!-- Advanced OVERLAYS accordion — Quadrant / Regression / Correlation /
         Cluster Scatter layer ON TOP of the selected base variant (Quadrant
         + Bubble, Regression + Scatter, …). Single-select rows; click the
         active one again to turn the overlay off. -->
    <div class="dvm-format-group wf-sb-advanced" [class.is-open]="sbAdvancedAccordionOpen" *ngIf="host.fieldEnabled('scatterBubble', 'advanced') && sbAdvancedVariantItems.length">
      <button type="button" class="dvm-format-group-head" (click)="sbAdvancedAccordionOpen = !sbAdvancedAccordionOpen">
        <span>{{ host.fieldLabel('scatterBubble', 'advanced', 'Multivariate Combinations') }}</span>
        <mat-icon>{{ sbAdvancedAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <div class="dvm-format-group-body wf-sb-advanced-list" *ngIf="sbAdvancedAccordionOpen">
        <button
          type="button"
          class="wf-sb-advanced-option"
          *ngFor="let v of sbAdvancedVariantItems"
          [class.is-active]="sbAdvanced === v.value"
          (click)="toggleSbAdvanced(v.value)"
        >
          <span class="wf-sb-advanced-label">{{ v.label }}</span>
          <mat-icon *ngIf="sbAdvanced === v.value">check</mat-icon>
        </button>
        <small class="control-hint wf-variant-hint" *ngIf="sbAdvancedHint">{{ sbAdvancedHint }}</small>
      </div>
    </div>
  </div>

  <!-- ══ Chart Data accordion — X / Y / Size / Color / Series ══ -->
  <div class="bar-options-accordion">
    <div class="dvm-format-group" [class.is-open]="sbDataAccordionOpen">
      <button type="button" class="dvm-format-group-head" (click)="sbDataAccordionOpen = !sbDataAccordionOpen">
        <span>Chart Data</span>
        <mat-icon>{{ sbDataAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <div class="dvm-format-group-body" *ngIf="sbDataAccordionOpen">

        <div class="control-row">
          <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onSbXFieldDrop($event)" title="Drag a column here">
            <label>X axis <span class="wf-axis-hint">— measure</span></label>
            <ng-select
              #ssSbX class="ss-select" [class.is-open]="!!ssSbX.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              [placeholder]="host.drillDataset ? 'Pick a column' : 'Select a dataset first'"
              [ngModel]="sbXField || null" (ngModelChange)="sbXField = $event || ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
          <div class="control-group">
            <label>X aggregation</label>
            <ng-select
              #ssSbXAgg class="ss-select" [class.is-open]="!!ssSbXAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="host.measureAggregations" bindLabel="label" bindValue="value" [clearable]="true"
              [ngModel]="sbXAggregation" (ngModelChange)="sbXAggregation = $event ?? ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
        </div>

        <div class="control-row">
          <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onSbYFieldDrop($event)" title="Drag a column here">
            <label>Y axis <span class="wf-axis-hint">— measure</span></label>
            <ng-select
              #ssSbY class="ss-select" [class.is-open]="!!ssSbY.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              [placeholder]="host.drillDataset ? 'Pick a column' : 'Select a dataset first'"
              [ngModel]="sbYField || null" (ngModelChange)="sbYField = $event || ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
          <div class="control-group">
            <label>Y aggregation</label>
            <ng-select
              #ssSbYAgg class="ss-select" [class.is-open]="!!ssSbYAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="host.measureAggregations" bindLabel="label" bindValue="value" [clearable]="true"
              [ngModel]="sbYAggregation" (ngModelChange)="sbYAggregation = $event ?? ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
        </div>

        <div class="control-row">
          <!-- Size — REQUIRED for Bubble variants only. -->
          <div class="control-group wf-field-drop-zone" [class.is-required-empty]="sbVariantUsesSize && !sbSizeField" *ngIf="sbVariantUsesSize" (dragover)="host.allowFieldDrop($event)" (drop)="onSbSizeFieldDrop($event)" title="Drag a column here">
            <label>Bubble size <span class="wf-axis-hint">(required)</span></label>
            <ng-select
              #ssSbSize class="ss-select" [class.is-open]="!!ssSbSize.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              placeholder="Pick a size measure"
              [ngModel]="sbSizeField || null" (ngModelChange)="sbSizeField = $event || ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
          <div class="control-group" *ngIf="sbVariantUsesSize">
            <label>Size aggregation</label>
            <ng-select
              #ssSbSizeAgg class="ss-select" [class.is-open]="!!ssSbSizeAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="host.measureAggregations" bindLabel="label" bindValue="value" [clearable]="true"
              [ngModel]="sbSizeAggregation" (ngModelChange)="sbSizeAggregation = $event ?? ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
          <!-- Color by — optional. -->
          <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onSbColorFieldDrop($event)" title="Drag a column here">
            <label>Color by <span class="wf-axis-hint">(optional)</span></label>
            <ng-select
              #ssSbColor class="ss-select" [class.is-open]="!!ssSbColor.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              placeholder="Optional grouping color"
              [ngModel]="sbColorField || null" (ngModelChange)="sbColorField = $event || ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
        </div>

        <div class="control-row">
          <!-- Series — REQUIRED for Multi Scatter only. -->
          <div class="control-group wf-field-drop-zone" [class.is-required-empty]="sbVariantUsesSeries && !sbSeries" *ngIf="sbVariantUsesSeries" (dragover)="host.allowFieldDrop($event)" (drop)="onSbSeriesFieldDrop($event)" title="Drag a column here">
            <label>Series / group <span class="wf-axis-hint">(required)</span></label>
            <ng-select
              #ssSbSeries class="ss-select" [class.is-open]="!!ssSbSeries.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              placeholder="Pick a column to split into series"
              [ngModel]="sbSeries || null" (ngModelChange)="sbSeries = $event || ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
        </div>

        <!-- Point label column (optional) — the text drawn on each point
             (an entity name). Config-driven via layoutSteps `labelField`. -->
        <div class="control-row" *ngIf="host.fieldEnabled('scatterBubble', 'labelField')">
          <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onSbLabelFieldDrop($event)" title="Drag a column here">
            <label>{{ host.fieldLabel('scatterBubble', 'labelField', 'Point label') }} <span class="wf-axis-hint">(optional)</span></label>
            <ng-select
              #ssSbLabel class="ss-select" [class.is-open]="!!ssSbLabel.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              placeholder="Column drawn as each point's label"
              [ngModel]="sbLabelField || null" (ngModelChange)="sbLabelField = $event || ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
        </div>

        <!-- Category colors — one swatch per distinct Color-by value. Unpicked
             values keep their palette slot (the renderer's own fallback), so
             the swatch always shows the color the chart is using. -->
        <div class="wf-sb-cat-colors" *ngIf="sbColorField && host.fieldEnabled('scatterBubble', 'categoryColors') && sbColorCategories.length">
          <div class="wf-sb-cat-colors-head">
            <span>{{ host.fieldLabel('scatterBubble', 'categoryColors', 'Category colors') }} <span class="wf-axis-hint">— {{ sbColorField }}</span></span>
            <button type="button" class="wf-sb-cat-colors-reset" *ngIf="hasSbCategoryColors" (click)="resetSbCategoryColors()">Reset to palette</button>
          </div>
          <div class="wf-sb-cat-colors-list">
            <label class="wf-sb-cat-color" *ngFor="let cat of sbColorCategories; let i = index" [title]="cat || '(blank)'">
              <input type="color" [ngModel]="sbCategoryColor(cat, i)" (ngModelChange)="onSbCategoryColorChange(cat, $event)" />
              <span class="wf-sb-cat-color-name">{{ cat || '(blank)' }}</span>
            </label>
          </div>
        </div>

        <!-- Per-axis label FONT WEIGHTS — highlight an axis's tick labels +
             title independently. '' = default (falls back to the Format
             popup's tile-wide "Axis label weight"). CONFIG-DRIVEN via
             layoutSteps entries in scatter-bubble.json; options from the
             shared shell `fontWeights` list. -->
        <div class="control-row" *ngIf="host.fieldEnabled('scatterBubble', 'xAxisFontWeight') || host.fieldEnabled('scatterBubble', 'yAxisFontWeight')">
          <div class="control-group" *ngIf="host.fieldEnabled('scatterBubble', 'xAxisFontWeight')">
            <label>{{ host.fieldLabel('scatterBubble', 'xAxisFontWeight', 'X axis label weight') }}</label>
            <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="sbXAxisFontWeight" (ngModelChange)="sbXAxisFontWeight = $event ?? ''; onScatterBubbleChange()"></ng-select>
          </div>
          <div class="control-group" *ngIf="host.fieldEnabled('scatterBubble', 'yAxisFontWeight')">
            <label>{{ host.fieldLabel('scatterBubble', 'yAxisFontWeight', 'Y axis label weight') }}</label>
            <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="sbYAxisFontWeight" (ngModelChange)="sbYAxisFontWeight = $event ?? ''; onScatterBubbleChange()"></ng-select>
          </div>
        </div>

        <div class="control-row">
          <!-- Aggregation — how rows sharing the same X (+ group) roll up. -->
          <div class="control-group" *ngIf="host.fieldEnabled('scatterBubble', 'aggregation')">
            <label>{{ host.fieldLabel('scatterBubble', 'aggregation', 'Aggregation') }} <span class="wf-axis-hint">— rows sharing X</span></label>
            <ng-select
              #ssSbAgg class="ss-select" [class.is-open]="!!ssSbAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="sbAggregationOptions" bindLabel="label" bindValue="value" [clearable]="true"
              [ngModel]="sbAggregation" (ngModelChange)="sbAggregation = $event ?? ''; onScatterBubbleChange()"
            ></ng-select>
          </div>
          <!-- Tooltip columns — extra dataset columns shown on hover. -->
          <div class="control-group" *ngIf="host.fieldEnabled('scatterBubble', 'tooltipColumns')">
            <label>{{ host.fieldLabel('scatterBubble', 'tooltipColumns', 'Tooltip columns') }} <span class="wf-axis-hint">(optional)</span></label>
            <ng-select
              #ssSbTooltipCols class="ss-select" [class.is-open]="!!ssSbTooltipCols.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="scatterBubbleColumns" [multiple]="true" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              placeholder="Extra columns on hover"
              [ngModel]="sbTooltipColumns" (ngModelChange)="sbTooltipColumns = $event || []; onScatterBubbleChange()"
            ></ng-select>
          </div>
        </div>

      </div><!-- /Chart Data body -->
    </div><!-- /Chart Data group -->
  </div><!-- /Chart Data accordion -->

  <!-- Scatter & Bubble Format accordions (config-driven; own sbFormat state). -->
  <div class="bar-options-accordion" *ngIf="scatterBubbleFormatGroups.length">
    <div class="dvm-format-group" *ngFor="let group of scatterBubbleFormatGroups" [class.is-open]="isSbFormatGroupOpen(group.name)">
      <button type="button" class="dvm-format-group-head" (click)="toggleSbFormatGroup(group.name)">
        <span>{{ group.name }}</span>
        <mat-icon>{{ isSbFormatGroupOpen(group.name) ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <div class="dvm-format-group-body" *ngIf="isSbFormatGroupOpen(group.name)">
        <ng-container *ngFor="let control of group.controls">
        <div class="dvm-format-row" *ngIf="host.formatControlVisible(control, sbFormat)">
          <label>{{ control.label }}</label>
          <input *ngIf="control.control === 'text'" type="text" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event; onScatterBubbleFormatChange()" [placeholder]="control.label" />
          <input *ngIf="control.control === 'number'" type="number" [min]="control.min ?? null" [max]="control.max ?? null" [step]="control.step ?? 1" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event; onScatterBubbleFormatChange()" />
          <input *ngIf="control.control === 'color'" type="color" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event; onScatterBubbleFormatChange()" />
          <label *ngIf="control.control === 'toggle'" class="dvm-format-toggle">
            <input type="checkbox" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event; onScatterBubbleFormatChange()" />
          </label>
          <ng-select *ngIf="control.control === 'select'" class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.formatControlOptions(control)" bindLabel="label" bindValue="value" [clearable]="true" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event ?? ''; onScatterBubbleFormatChange()"></ng-select>
        </div>
        </ng-container>
      </div>
    </div>
  </div>
</div>
